/** Restricted-access indicator and editor on the asset page. */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Lock, LockOpen } from 'lucide-react';
import type { AssetDetail } from '@prism/contracts';
import { api, errorMessage } from '../api/client';
import { useAccessGroups, useMe } from '../api/hooks';
import { Modal } from '../components/Modal';
import { toast } from '../components/Toasts';
import { queryKeys } from '../api/queryKeys';

export function AssetAccess({ asset }: { asset: AssetDetail }) {
  const me = useMe().data!;
  const canManage = me.permissions.includes('access.manage');
  const [open, setOpen] = useState(false);
  const label = asset.restrictedTo.length ? `Restricted: ${asset.restrictedTo.map((g) => g.name).join(', ')}` : 'Restricted';
  if (!canManage) return asset.restricted ? <span className="pill" data-testid="restricted-pill"><Lock size={12} style={{ marginRight: 5 }} />{label}</span> : null;
  return (
    <>
      <button className={`btn sm ${asset.restricted ? '' : 'ghost'}`} onClick={() => setOpen(true)} data-testid="asset-access" title="Who can see this asset">
        {asset.restricted ? <Lock size={14} color="var(--warning)" /> : <LockOpen size={14} />} {asset.restricted ? label : 'Everyone'}
      </button>
      {open && <AccessModal asset={asset} onClose={() => setOpen(false)} />}
    </>
  );
}

function AccessModal({ asset, onClose }: { asset: AssetDetail; onClose: () => void }) {
  const groups = useAccessGroups().data?.items ?? [];
  const qc = useQueryClient();
  const [chosen, setChosen] = useState<string[]>(asset.restrictedTo.map((g) => g.id));
  const [busy, setBusy] = useState(false);
  async function save() {
    setBusy(true);
    try {
      const next = await api<AssetDetail>(`/api/v1/assets/${asset.id}/access`, { method: 'PUT', json: { groupIds: chosen } });
      qc.setQueryData(queryKeys.asset(asset.id), next);
      void qc.invalidateQueries({ queryKey: queryKeys.assets() });
      toast(chosen.length ? 'Access restricted' : 'Visible to everyone in the workspace');
      onClose();
    } catch (err) {
      toast(errorMessage(err), 'error');
      setBusy(false);
    }
  }
  return (
    <Modal title="Who can see this asset" onClose={onClose} actions={<><button className="btn ghost" onClick={onClose}>Cancel</button><button className="btn primary" disabled={busy} onClick={() => void save()} data-testid="save-access">Save</button></>}>
      <p className="muted" style={{ marginTop: 0 }}>With no group selected, everyone in the workspace can see it. Otherwise only members of the chosen groups, and people who manage access, can find, open or play it. Links already shared stop working immediately.</p>
      {groups.length === 0 && <p className="muted">Create access groups under Administration → Access first.</p>}
      {groups.map((g) => (
        <label key={g.id} className="option-row">
          <input type="checkbox" checked={chosen.includes(g.id)} onChange={(e) => setChosen(e.target.checked ? [...chosen, g.id] : chosen.filter((x) => x !== g.id))} />
          {g.name} <span className="faint">· {g.memberCount} {g.memberCount === 1 ? 'member' : 'members'}</span>
        </label>
      ))}
    </Modal>
  );
}
